import React, { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../services/context';
import PageHeader from '../../Components/PageHeader';
import subscriptionPlanService from '../../services/subscriptionPlanService';
import PlanClassesManager from '../../Components/PlanClassesManager';

/**
 * Interface de gestion des formules d'abonnement
 * Permet de créer, modifier et supprimer les plans d'abonnement
 */
const SubscriptionPlansManager = () => {
  const { user } = useContext(AppContext);
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filtres
  const [filters, setFilters] = useState({
    status: '',
    price_range: '',
    search: ''
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showClassesModal, setShowClassesModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Formulaire de création/édition
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'XOF',
    duration_days: '',
    billing_cycle: 'monthly',
    max_students: '',
    max_classes: '',
    max_subjects: '',
    trial_days: '',
    setup_fee: '',
    discount_percentage: '',
    analytics_access: true,
    priority_support: false,
    offline_access: true,
    custom_branding: false,
    features: [],
    restrictions: [],
    is_active: true,
    is_featured: false,
    sort_order: 1
  });

  // Options de cycle de facturation
  const billingCycleOptions = [
    { value: 'weekly', label: '1 semaine', days: 7 },
    { value: 'monthly', label: '1 mois', days: 30 },
    { value: 'quarterly', label: '3 mois', days: 90 },
    { value: 'semi_annual', label: '6 mois', days: 180 },
    { value: 'yearly', label: '1 an', days: 365 },
    { value: 'custom', label: 'Personnalisé', days: 0 }
  ];

  // Liste des fonctionnalités disponibles
  const availableFeatures = [
    'Accès à tous les cours',
    'Support prioritaire',
    'Accès hors ligne',
    'Analyses avancées',
    'Personnalisation de marque',
    'Évaluations illimitées',
    'Rapports détaillés',
    'Intégration API',
    'Sauvegarde automatique',
    'Multi-utilisateurs'
  ];

  // Liste des restrictions disponibles
  const availableRestrictions = [
    'Accès limité aux cours basiques',
    'Support standard uniquement',
    'Pas d\'accès hors ligne',
    'Analyses limitées',
    'Pas de personnalisation',
    'Évaluations limitées',
    'Rapports basiques uniquement',
    'Pas d\'intégration API',
    'Sauvegarde manuelle',
    'Utilisateur unique'
  ];

  const currencies = [
    { value: 'XOF', label: 'XOF (Franc CFA)' },
    /*{ value: 'EUR', label: 'EUR (Euro)' },
    { value: 'USD', label: 'USD (Dollar)' }*/
  ];

  const priceRanges = [
    { value: '', label: 'Toutes les gammes de prix' },
    { value: '0-25000', label: '0 - 25 000 XOF' },
    { value: '25000-50000', label: '25 000 - 50 000 XOF' },
    { value: '50000-100000', label: '50 000 - 100 000 XOF' },
    { value: '100000+', label: '100 000+ XOF' }
  ];

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await subscriptionPlanService.getAll({
        page: currentPage,
        per_page: 12
      });

      if (response.success) {
        const planData = Array.isArray(response.data?.data) 
          ? response.data.data 
          : Array.isArray(response.data) 
          ? response.data 
          : [];
        
        setPlans(planData);
        setTotalPages(response.data?.last_page || 1);
        toast.success('Formules chargées avec succès');
      } else {
        throw new Error(response.message || 'Erreur lors du chargement');
      }
    } catch (error) {
      console.error('Erreur lors du chargement des formules:', error);
      toast.error('Erreur lors du chargement des formules');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  const applyFilters = useCallback(() => {
    let filtered = [...plans];

    if (filters.status) {
      filtered = filtered.filter(plan => 
        filters.status === 'active' ? plan.is_active : !plan.is_active
      );
    }

    if (filters.price_range) {
      const [min, max] = filters.price_range.split('-').map(Number);
      if (max) {
        filtered = filtered.filter(plan => 
          plan.price >= min && plan.price <= max
        );
      } else if (filters.price_range.includes('+')) {
        filtered = filtered.filter(plan => plan.price >= min);
      }
    }

    if (filters.search) {
      filtered = filtered.filter(plan =>
        plan.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        plan.description?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    setFilteredPlans(filtered);
  }, [plans, filters]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Fonction pour gérer le changement de cycle de facturation
  const handleBillingCycleChange = (cycle) => {
    const selectedOption = billingCycleOptions.find(option => option.value === cycle);
    setPlanForm({
      ...planForm,
      billing_cycle: cycle,
      duration_days: selectedOption?.days || planForm.duration_days
    });
  };

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    
    // Validation côté client
    if (!planForm.name?.trim()) {
      toast.error('Le nom de la formule est obligatoire');
      return;
    }
    
    if (!planForm.price || parseFloat(planForm.price) <= 0) {
      toast.error('Le prix doit être supérieur à 0');
      return;
    }
    
    if (!planForm.duration_days || parseInt(planForm.duration_days) <= 0) {
      toast.error('La durée doit être supérieure à 0 jour');
      return;
    }
    
    if (!planForm.billing_cycle) {
      toast.error('Le cycle de facturation est obligatoire');
      return;
    }
    
    // Validation des limites
    if (planForm.max_students !== '' && planForm.max_students !== -1 && parseInt(planForm.max_students) <= 0) {
      toast.error('Le nombre maximum d\'élèves doit être supérieur à 0 ou vide pour illimité');
      return;
    }
    
    try {
      const response = await subscriptionPlanService.create(planForm);
      if (response.success) {
        toast.success('Formule créée avec succès');
        setShowCreateModal(false);
        resetForm();
        loadPlans();
      }
    } catch (error) {
      console.error('Erreur création:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la création');
    }
  };

  const handleEditPlan = async (e) => {
    e.preventDefault();
    
    // Validation côté client
    if (!planForm.name?.trim()) {
      toast.error('Le nom de la formule est obligatoire');
      return;
    }
    
    if (!planForm.price || parseFloat(planForm.price) <= 0) {
      toast.error('Le prix doit être supérieur à 0');
      return;
    }
    
    if (!planForm.duration_days || parseInt(planForm.duration_days) <= 0) {
      toast.error('La durée doit être supérieure à 0 jour');
      return;
    }
    
    if (!planForm.billing_cycle) {
      toast.error('Le cycle de facturation est obligatoire');
      return;
    }
    
    // Validation des limites
    if (planForm.max_students !== '' && planForm.max_students !== -1 && parseInt(planForm.max_students) <= 0) {
      toast.error('Le nombre maximum d\'élèves doit être supérieur à 0 ou vide pour illimité');
      return;
    }
    
    try {
      const response = await subscriptionPlanService.update(selectedPlan.slug, planForm);
      if (response.success) {
        toast.success('Formule mise à jour avec succès');
        setShowEditModal(false);
        resetForm();
        loadPlans();
      }
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeletePlan = async () => {
    try {
      const response = await subscriptionPlanService.delete(selectedPlan.id);
      if (response.success) {
        toast.success('Formule supprimée avec succès');
        setShowDeleteModal(false);
        setSelectedPlan(null);
        loadPlans();
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  // Gestion des classes d'un plan
  const handleShowClasses = (plan) => {
    setSelectedPlan(plan);
    setShowClassesModal(true);
  };

  const handleCloseClassesModal = () => {
    setShowClassesModal(false);
    setSelectedPlan(null);
  };

  const handleToggleStatus = async (plan) => {
    try {
      const response = await subscriptionPlanService.update(plan.id, {
        ...plan,
        is_active: !plan.is_active
      });
      if (response.success) {
        toast.success(`Formule ${!plan.is_active ? 'activée' : 'désactivée'}`);
        loadPlans();
      }
    } catch (error) {
      console.error('Erreur changement de statut:', error);
      toast.error('Erreur lors du changement de statut');
    }
  };

  const openEditModal = (plan) => {
    setSelectedPlan(plan);
    
    // Déterminer le billing_cycle basé sur duration_days
    let billing_cycle = plan.billing_cycle || 'custom';
    if (!plan.billing_cycle) {
      const days = plan.duration_days;
      if (days === 7) billing_cycle = 'weekly';
      else if (days === 30) billing_cycle = 'monthly';
      else if (days === 90) billing_cycle = 'quarterly';
      else if (days === 180) billing_cycle = 'semi_annual';
      else if (days === 365) billing_cycle = 'yearly';
    }
    
    setPlanForm({
      name: plan.name || '',
      description: plan.description || '',
      price: plan.price || '',
      currency: plan.currency || 'XOF',
      duration_days: plan.duration_days || '',
      billing_cycle: billing_cycle,
      max_students: plan.max_students || '',
      max_classes: plan.max_classes || '',
      max_subjects: plan.max_subjects || '',
      trial_days: plan.trial_days || '',
      setup_fee: plan.setup_fee || '',
      discount_percentage: plan.discount_percentage || '',
      analytics_access: plan.analytics_access !== undefined ? plan.analytics_access : true,
      priority_support: plan.priority_support || false,
      offline_access: plan.offline_access !== undefined ? plan.offline_access : true,
      custom_branding: plan.custom_branding || false,
      features: Array.isArray(plan.features) ? plan.features : [],
      features_detailed: Array.isArray(plan.features_detailed) ? plan.features_detailed : [],
      restrictions: Array.isArray(plan.restrictions) ? plan.restrictions : [],
      is_active: plan.is_active !== undefined ? plan.is_active : true,
      is_featured: plan.is_featured || false,
      sort_order: plan.sort_order || 1
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setPlanForm({
      name: '',
      description: '',
      price: '',
      currency: 'XOF',
      duration_days: '',
      billing_cycle: 'monthly',
      max_students: '',
      max_classes: '',
      max_subjects: '',
      trial_days: '',
      setup_fee: '',
      discount_percentage: '',
      analytics_access: true,
      priority_support: false,
      offline_access: true,
      custom_branding: false,
      features: [],
      features_detailed: [],
      restrictions: [],
      is_active: true,
      is_featured: false,
      sort_order: 1
    });
    setSelectedPlan(null);
  };

  const handleFeatureToggle = (feature) => {
    const currentFeatures = [...planForm.features];
    const index = currentFeatures.indexOf(feature);
    
    if (index > -1) {
      currentFeatures.splice(index, 1);
    } else {
      currentFeatures.push(feature);
    }
    
    setPlanForm({...planForm, features: currentFeatures});
  };

  const handleFeatureDetailedToggle = (feature) => {
    const currentFeatures = [...(planForm.features_detailed || [])];
    const index = currentFeatures.indexOf(feature);
    
    if (index > -1) {
      currentFeatures.splice(index, 1);
    } else {
      currentFeatures.push(feature);
    }
    
    setPlanForm({...planForm, features_detailed: currentFeatures});
  };

  const formatPrice = (price, currency = 'XOF') => {
    return new Intl.NumberFormat('fr-FR').format(price) + ' ' + currency;
  };

  const formatDuration = (days) => {
    if (days >= 365) {
      const years = Math.floor(days / 365);
      const months = Math.floor((days % 365) / 30);
      return years > 0 ? `${years} an${years > 1 ? 's' : ''}${months > 0 ? ` ${months} mois` : ''}` : `${months} mois`;
    } else if (days >= 30) {
      const months = Math.floor(days / 30);
      return `${months} mois`;
    } else {
      return `${days} jour${days > 1 ? 's' : ''}`;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <PageHeader title="Gestion des Formules d'Abonnement" />

      {/* Filtres et Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-3">
                  <label className="form-label">Statut</label>
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="active">Actives</option>
                    <option value="inactive">Inactives</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Gamme de Prix</label>
                  <select
                    className="form-select"
                    value={filters.price_range}
                    onChange={(e) => setFilters({...filters, price_range: e.target.value})}
                  >
                    {priceRanges.map(range => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Recherche</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Nom ou description..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                  />
                </div>

                <div className="col-md-2">
                  {user.permissions?.includes('create subscription plans') && (
                    <button
                      className="btn btn-primary w-100"
                      onClick={() => setShowCreateModal(true)}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Nouvelle Formule
                    </button>
                  )}
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setFilters({ status: '', price_range: '', search: '' })}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Réinitialiser les filtres
                  </button>
                  <span className="ms-3 text-muted">
                    {filteredPlans.length} formule{filteredPlans.length > 1 ? 's' : ''} trouvée{filteredPlans.length > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grille des Formules */}
      <div className="row">
        {filteredPlans.map(plan => (
          <div key={plan.id} className="col-lg-4 col-md-6 mb-4">
            <div className={`card h-100 ${plan.is_active ? 'border-primary' : 'border-secondary'}`}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{plan.name}</h5>
                <div className="d-flex gap-2">
                  <span className={`badge ${plan.is_active ? 'bg-success' : 'bg-secondary'}`}>
                    {plan.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {plan.trial_days > 0 && (
                    <span className="badge bg-info">
                      {plan.trial_days}j d'essai
                    </span>
                  )}
                </div>
              </div>
              
              <div className="card-body">
                <div className="text-center mb-3">
                  <h3 className="text-primary">
                    {formatPrice(plan.price, plan.currency)}
                  </h3>
                  <small className="text-muted">
                    pour {formatDuration(plan.duration_days)}
                  </small>
                  {plan.setup_fee > 0 && (
                    <div className="small text-warning">
                      + {formatPrice(plan.setup_fee, plan.currency)} frais d'activation
                    </div>
                  )}
                  {plan.discount_percentage > 0 && (
                    <div className="small text-success">
                      -{plan.discount_percentage}% de remise
                    </div>
                  )}
                </div>

                <p className="text-muted small">
                  {plan.description || 'Aucune description'}
                </p>

                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <strong className="text-primary">
                      <i className="bi bi-people me-1"></i>
                      {plan.max_students || 'Illimité'} élève{plan.max_students > 1 ? 's' : ''}
                    </strong>
                    <strong className="text-info">
                      <i className="bi bi-collection me-1"></i>
                      {plan.max_classes || 'Illimité'} classe{plan.max_classes > 1 ? 's' : ''}
                    </strong>
                  </div>
                </div>

                {plan.features && plan.features.length > 0 && (
                  <div className="mb-3">
                    <small className="text-muted fw-bold">Fonctionnalités incluses :</small>
                    <ul className="list-unstyled small mt-2">
                      {plan.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="mb-1">
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          {feature}
                        </li>
                      ))}
                      {plan.features.length > 4 && (
                        <li className="text-muted">
                          <i className="bi bi-three-dots me-2"></i>
                          +{plan.features.length - 4} autres fonctionnalités
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="card-footer">
                <div className="btn-group w-100">
                  {user.permissions?.includes('update subscription plans') && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => openEditModal(plan)}
                    >
                      <i className="bi bi-pencil"></i>
                      Modifier
                    </button>
                  )}

                  <button
                    className="btn btn-outline-info btn-sm"
                    onClick={() => handleShowClasses(plan)}
                    title="Gérer les classes"
                  >
                    <i className="bi bi-collection"></i>
                    Classes
                  </button>

                  <button
                    className={`btn btn-sm ${plan.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                    onClick={() => handleToggleStatus(plan)}
                    title={plan.is_active ? 'Désactiver' : 'Activer'}
                  >
                    <i className={`bi ${plan.is_active ? 'bi-pause-circle' : 'bi-play-circle'}`}></i>
                    {plan.is_active ? 'Désactiver' : 'Activer'}
                  </button>

                  {user.permissions?.includes('delete subscription plans') && (
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => {
                        setSelectedPlan(plan);
                        setShowDeleteModal(true);
                      }}
                    >
                      <i className="bi bi-trash"></i>
                      Supprimer
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {filteredPlans.length === 0 && (
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="bi bi-tags display-1 text-muted"></i>
                <h4 className="text-muted mt-3">Aucune formule trouvée</h4>
                <p className="text-muted">
                  {plans.length === 0 
                    ? "Aucune formule d'abonnement n'a encore été créée."
                    : "Aucune formule ne correspond à vos critères de recherche."
                  }
                </p>
                {user.permissions?.includes('create subscription plans') && (
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Créer la première formule
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="row mt-4">
          <div className="col-12">
            <nav>
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </button>
                </li>
                
                {[...Array(totalPages)].map((_, index) => (
                  <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setCurrentPage(index + 1)}
                    >
                      {index + 1}
                    </button>
                  </li>
                ))}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {/* Modal de Création */}
      {showCreateModal && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Créer une Nouvelle Formule</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                ></button>
              </div>
              <form onSubmit={handleCreatePlan}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Nom de la formule *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={planForm.name}
                          onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Prix *</label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            value={planForm.price}
                            onChange={(e) => setPlanForm({...planForm, price: e.target.value})}
                            required
                            min="0"
                          />
                          <select
                            className="form-select"
                            style={{maxWidth: '100px'}}
                            value={planForm.currency}
                            onChange={(e) => setPlanForm({...planForm, currency: e.target.value})}
                          >
                            {currencies.map(curr => (
                              <option key={curr.value} value={curr.value}>
                                {curr.value}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Cycle de facturation *</label>
                        <select
                          className="form-select"
                          value={planForm.billing_cycle}
                          onChange={(e) => handleBillingCycleChange(e.target.value)}
                          required
                        >
                          {billingCycleOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <small className="text-muted">
                          Sélectionnez la fréquence de facturation
                        </small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Durée (en jours) *
                          {planForm.billing_cycle === 'custom' ? ' - Personnalisée' : ''}
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.duration_days}
                          onChange={(e) => setPlanForm({...planForm, duration_days: e.target.value})}
                          required
                          min="1"
                          disabled={planForm.billing_cycle !== 'custom'}
                        />
                        <small className="text-muted">
                          {planForm.billing_cycle === 'custom' 
                            ? 'Entrez le nombre de jours personnalisé'
                            : 'Durée automatiquement définie par le cycle de facturation'
                          }
                        </small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Nombre max d'élèves</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.max_students}
                          onChange={(e) => setPlanForm({...planForm, max_students: e.target.value})}
                          min="-1"
                          placeholder="Laisser vide ou -1 pour illimité"
                        />
                        <small className="text-muted">-1 pour illimité</small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Nombre max de classes</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.max_classes}
                          onChange={(e) => setPlanForm({...planForm, max_classes: e.target.value})}
                          min="-1"
                          placeholder="Laisser vide ou -1 pour illimité"
                        />
                        <small className="text-muted">-1 pour illimité</small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Nombre max de matières</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.max_subjects}
                          onChange={(e) => setPlanForm({...planForm, max_subjects: e.target.value})}
                          min="-1"
                          placeholder="Laisser vide ou -1 pour illimité"
                        />
                        <small className="text-muted">-1 pour illimité</small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Ordre de tri</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.sort_order}
                          onChange={(e) => setPlanForm({...planForm, sort_order: e.target.value})}
                          min="0"
                          placeholder="1"
                        />
                        <small className="text-muted">Plus petit = affiché en premier</small>
                      </div>
                    </div>

                    {/* Nouveaux champs ajoutés au formulaire de création */}
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Jours d'essai gratuit</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.trial_days}
                          onChange={(e) => setPlanForm({...planForm, trial_days: e.target.value})}
                          min="0"
                          placeholder="0"
                        />
                        <small className="text-muted">0 pour aucun essai gratuit</small>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Frais d'activation</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.setup_fee}
                          onChange={(e) => setPlanForm({...planForm, setup_fee: e.target.value})}
                          min="0"
                          placeholder="0"
                        />
                        <small className="text-muted">Frais uniques à l'activation</small>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Remise (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.discount_percentage}
                          onChange={(e) => setPlanForm({...planForm, discount_percentage: e.target.value})}
                          min="0"
                          max="100"
                          placeholder="0"
                        />
                        <small className="text-muted">Pourcentage de réduction</small>
                      </div>
                    </div>

                    {/* Fonctionnalités booléennes */}
                    <div className="col-12">
                      <h6 className="mb-3">Fonctionnalités incluses</h6>
                      <div className="row">
                        <div className="col-md-3">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={planForm.analytics_access}
                              onChange={(e) => setPlanForm({...planForm, analytics_access: e.target.checked})}
                              id="analytics_access"
                            />
                            <label className="form-check-label" htmlFor="analytics_access">
                              Accès aux analyses
                            </label>
                          </div>
                        </div>
                        
                        <div className="col-md-3">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={planForm.priority_support}
                              onChange={(e) => setPlanForm({...planForm, priority_support: e.target.checked})}
                              id="priority_support"
                            />
                            <label className="form-check-label" htmlFor="priority_support">
                              Support prioritaire
                            </label>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={planForm.offline_access}
                              onChange={(e) => setPlanForm({...planForm, offline_access: e.target.checked})}
                              id="offline_access"
                            />
                            <label className="form-check-label" htmlFor="offline_access">
                              Accès hors ligne
                            </label>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={planForm.custom_branding}
                              onChange={(e) => setPlanForm({...planForm, custom_branding: e.target.checked})}
                              id="custom_branding"
                            />
                            <label className="form-check-label" htmlFor="custom_branding">
                              Personnalisation marque
                            </label>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={planForm.is_featured}
                              onChange={(e) => setPlanForm({...planForm, is_featured: e.target.checked})}
                              id="is_featured"
                            />
                            <label className="form-check-label" htmlFor="is_featured">
                              Plan mis en avant
                            </label>
                          </div>
                        </div>

                        <div className="col-md-3">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={planForm.is_active}
                              onChange={(e) => setPlanForm({...planForm, is_active: e.target.checked})}
                              id="is_active_create"
                            />
                            <label className="form-check-label" htmlFor="is_active_create">
                              Plan actif
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={planForm.description}
                          onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                          placeholder="Décrivez cette formule d'abonnement..."
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">Fonctionnalités détaillées incluses</label>
                        <div className="row">
                          {availableFeatures.map((feature, idx) => (
                            <div key={idx} className="col-md-6">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={planForm.features.includes(feature)}
                                  onChange={() => handleFeatureToggle(feature)}
                                  id={`feature_${idx}_create`}
                                />
                                <label className="form-check-label" htmlFor={`feature_${idx}_create`}>
                                  {feature}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Créer la Formule
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'Édition */}
      {showEditModal && selectedPlan && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Modifier la Formule</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                ></button>
              </div>
              <form onSubmit={handleEditPlan}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Nom de la formule *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={planForm.name}
                          onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Prix *</label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            value={planForm.price}
                            onChange={(e) => setPlanForm({...planForm, price: e.target.value})}
                            required
                            min="0"
                          />
                          <select
                            className="form-select"
                            style={{maxWidth: '100px'}}
                            value={planForm.currency}
                            onChange={(e) => setPlanForm({...planForm, currency: e.target.value})}
                          >
                            {currencies.map(curr => (
                              <option key={curr.value} value={curr.value}>
                                {curr.value}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Cycle de facturation *</label>
                        <select
                          className="form-select"
                          value={planForm.billing_cycle}
                          onChange={(e) => handleBillingCycleChange(e.target.value)}
                          required
                        >
                          {billingCycleOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Durée (en jours) *
                          {planForm.billing_cycle === 'custom' ? ' - Personnalisée' : ''}
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.duration_days}
                          onChange={(e) => setPlanForm({...planForm, duration_days: e.target.value})}
                          required
                          min="1"
                          disabled={planForm.billing_cycle !== 'custom'}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Nombre max d'élèves</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.max_students}
                          onChange={(e) => setPlanForm({...planForm, max_students: e.target.value})}
                          min="-1"
                          placeholder="Laisser vide ou -1 pour illimité"
                        />
                        <small className="text-muted">-1 pour illimité</small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Nombre max de classes</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.max_classes}
                          onChange={(e) => setPlanForm({...planForm, max_classes: e.target.value})}
                          min="-1"
                          placeholder="Laisser vide ou -1 pour illimité"
                        />
                        <small className="text-muted">-1 pour illimité</small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Nombre max de matières</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.max_subjects}
                          onChange={(e) => setPlanForm({...planForm, max_subjects: e.target.value})}
                          min="-1"
                          placeholder="Laisser vide ou -1 pour illimité"
                        />
                        <small className="text-muted">-1 pour illimité</small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Ordre de tri</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.sort_order}
                          onChange={(e) => setPlanForm({...planForm, sort_order: e.target.value})}
                          min="0"
                          placeholder="1"
                        />
                        <small className="text-muted">Plus petit = affiché en premier</small>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Jours d'essai gratuit</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.trial_days}
                          onChange={(e) => setPlanForm({...planForm, trial_days: e.target.value})}
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Frais d'activation</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.setup_fee}
                          onChange={(e) => setPlanForm({...planForm, setup_fee: e.target.value})}
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="mb-3">
                        <label className="form-label">Remise (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={planForm.discount_percentage}
                          onChange={(e) => setPlanForm({...planForm, discount_percentage: e.target.value})}
                          min="0"
                          max="100"
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={planForm.description}
                          onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Nombre de jours d'essai</label>
                          <input
                            type="number"
                            className="form-control"
                            value={planForm.trial_days}
                            onChange={(e) => setPlanForm({...planForm, trial_days: e.target.value})}
                            min="0"
                            placeholder="0"
                          />
                          <small className="text-muted">0 = pas d'essai gratuit</small>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Frais d'installation (€)</label>
                          <input
                            type="number"
                            className="form-control"
                            step="0.01"
                            value={planForm.setup_fee}
                            onChange={(e) => setPlanForm({...planForm, setup_fee: e.target.value})}
                            min="0"
                            placeholder="0.00"
                          />
                          <small className="text-muted">Frais unique à l'activation</small>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Pourcentage de réduction (%)</label>
                          <input
                            type="number"
                            className="form-control"
                            value={planForm.discount_percentage}
                            onChange={(e) => setPlanForm({...planForm, discount_percentage: e.target.value})}
                            min="0"
                            max="100"
                            placeholder="0"
                          />
                          <small className="text-muted">Réduction promotionnelle temporaire</small>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3 form-check mt-4">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="editFeatured"
                            checked={planForm.is_featured}
                            onChange={(e) => setPlanForm({...planForm, is_featured: e.target.checked})}
                          />
                          <label className="form-check-label" htmlFor="editFeatured">
                            Plan mis en avant
                          </label>
                          <small className="text-muted d-block">Sera affiché avec un badge "Recommandé"</small>
                        </div>
                      </div>
                    </div>

                    <hr />
                    
                    <h6 className="mb-3">Fonctionnalités incluses</h6>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3 form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="editAnalyticsAccess"
                            checked={planForm.analytics_access}
                            onChange={(e) => setPlanForm({...planForm, analytics_access: e.target.checked})}
                          />
                          <label className="form-check-label" htmlFor="editAnalyticsAccess">
                            Accès aux analyses avancées
                          </label>
                          <small className="text-muted d-block">Statistiques détaillées et rapports</small>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3 form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="editPrioritySupport"
                            checked={planForm.priority_support}
                            onChange={(e) => setPlanForm({...planForm, priority_support: e.target.checked})}
                          />
                          <label className="form-check-label" htmlFor="editPrioritySupport">
                            Support prioritaire
                          </label>
                          <small className="text-muted d-block">Support technique en moins de 24h</small>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3 form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="editOfflineAccess"
                            checked={planForm.offline_access}
                            onChange={(e) => setPlanForm({...planForm, offline_access: e.target.checked})}
                          />
                          <label className="form-check-label" htmlFor="editOfflineAccess">
                            Accès hors ligne
                          </label>
                          <small className="text-muted d-block">Utilisation sans connexion internet</small>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3 form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="editCustomBranding"
                            checked={planForm.custom_branding}
                            onChange={(e) => setPlanForm({...planForm, custom_branding: e.target.checked})}
                          />
                          <label className="form-check-label" htmlFor="editCustomBranding">
                            Image de marque personnalisée
                          </label>
                          <small className="text-muted d-block">Logo et couleurs de votre établissement</small>
                        </div>
                      </div>
                    </div>

                    <hr />

                    <h6 className="mb-3">Fonctionnalités détaillées</h6>
                    
                    <div className="mb-3">
                      <label className="form-label">Sélectionnez les fonctionnalités disponibles pour ce plan :</label>
                      
                      <div className="row mt-3">
                        <div className="col-md-4">
                          <h6 className="text-muted">Gestion des cours</h6>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="editFeatureCourses"
                              checked={planForm.features_detailed?.includes('courses')}
                              onChange={(e) => {
                                const features = planForm.features_detailed || [];
                                if (e.target.checked) {
                                  setPlanForm({...planForm, features_detailed: [...features, 'courses']});
                                } else {
                                  setPlanForm({...planForm, features_detailed: features.filter(f => f !== 'courses')});
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="editFeatureCourses">
                              Création de cours
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="editFeatureQuizzes"
                              checked={planForm.features_detailed?.includes('quizzes')}
                              onChange={(e) => {
                                const features = planForm.features_detailed || [];
                                if (e.target.checked) {
                                  setPlanForm({...planForm, features_detailed: [...features, 'quizzes']});
                                } else {
                                  setPlanForm({...planForm, features_detailed: features.filter(f => f !== 'quizzes')});
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="editFeatureQuizzes">
                              Quiz et évaluations
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="editFeatureAssignments"
                              checked={planForm.features_detailed?.includes('assignments')}
                              onChange={(e) => {
                                const features = planForm.features_detailed || [];
                                if (e.target.checked) {
                                  setPlanForm({...planForm, features_detailed: [...features, 'assignments']});
                                } else {
                                  setPlanForm({...planForm, features_detailed: features.filter(f => f !== 'assignments')});
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="editFeatureAssignments">
                              Devoirs et travaux
                            </label>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <h6 className="text-muted">Communication</h6>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="editFeatureMessaging"
                              checked={planForm.features_detailed?.includes('messaging')}
                              onChange={(e) => {
                                const features = planForm.features_detailed || [];
                                if (e.target.checked) {
                                  setPlanForm({...planForm, features_detailed: [...features, 'messaging']});
                                } else {
                                  setPlanForm({...planForm, features_detailed: features.filter(f => f !== 'messaging')});
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="editFeatureMessaging">
                              Messagerie interne
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="editFeatureNotifications"
                              checked={planForm.features_detailed?.includes('notifications')}
                              onChange={(e) => {
                                const features = planForm.features_detailed || [];
                                if (e.target.checked) {
                                  setPlanForm({...planForm, features_detailed: [...features, 'notifications']});
                                } else {
                                  setPlanForm({...planForm, features_detailed: features.filter(f => f !== 'notifications')});
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="editFeatureNotifications">
                              Notifications push
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="editFeatureAnnouncements"
                              checked={planForm.features_detailed?.includes('announcements')}
                              onChange={(e) => {
                                const features = planForm.features_detailed || [];
                                if (e.target.checked) {
                                  setPlanForm({...planForm, features_detailed: [...features, 'announcements']});
                                } else {
                                  setPlanForm({...planForm, features_detailed: features.filter(f => f !== 'announcements')});
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="editFeatureAnnouncements">
                              Annonces générales
                            </label>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <h6 className="text-muted">Fonctionnalités avancées</h6>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="editFeatureReports"
                              checked={planForm.features_detailed?.includes('reports')}
                              onChange={(e) => {
                                const features = planForm.features_detailed || [];
                                if (e.target.checked) {
                                  setPlanForm({...planForm, features_detailed: [...features, 'reports']});
                                } else {
                                  setPlanForm({...planForm, features_detailed: features.filter(f => f !== 'reports')});
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="editFeatureReports">
                              Rapports détaillés
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="editFeatureIntegrations"
                              checked={planForm.features_detailed?.includes('integrations')}
                              onChange={(e) => {
                                const features = planForm.features_detailed || [];
                                if (e.target.checked) {
                                  setPlanForm({...planForm, features_detailed: [...features, 'integrations']});
                                } else {
                                  setPlanForm({...planForm, features_detailed: features.filter(f => f !== 'integrations')});
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="editFeatureIntegrations">
                              Intégrations tierces
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="editFeatureApiAccess"
                              checked={planForm.features_detailed?.includes('api_access')}
                              onChange={(e) => {
                                const features = planForm.features_detailed || [];
                                if (e.target.checked) {
                                  setPlanForm({...planForm, features_detailed: [...features, 'api_access']});
                                } else {
                                  setPlanForm({...planForm, features_detailed: features.filter(f => f !== 'api_access')});
                                }
                              }}
                            />
                            <label className="form-check-label" htmlFor="editFeatureApiAccess">
                              Accès API
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={planForm.is_active}
                          onChange={(e) => setPlanForm({...planForm, is_active: e.target.checked})}
                        />
                        <label className="form-check-label">
                          Formule active
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEditModal(false);
                      resetForm();
                    }}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Sauvegarder les Modifications
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Suppression */}
      {showDeleteModal && selectedPlan && (
        <div className="modal fade show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmer la Suppression</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedPlan(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center">
                  <i className="bi bi-exclamation-triangle display-1 text-warning"></i>
                  <h4 className="mt-3">Êtes-vous sûr ?</h4>
                  <p className="text-muted">
                    Vous êtes sur le point de supprimer la formule <strong>"{selectedPlan.name}"</strong>.
                    Cette action est irréversible.
                  </p>
                  <div className="alert alert-warning">
                    <strong>Attention :</strong> La suppression de cette formule pourrait affecter 
                    les abonnements existants qui l'utilisent.
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedPlan(null);
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeletePlan}
                >
                  Oui, Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de gestion des classes */}
      <PlanClassesManager
        plan={selectedPlan}
        isVisible={showClassesModal}
        onClose={handleCloseClassesModal}
      />
    </div>
  );
};

export default SubscriptionPlansManager;
